import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';

// Custom domains serve Firebase's /__/auth/* handler themselves, so sign-in on
// one stays first-party when the browser blocks third-party cookies. Every
// other hostname (preview channels, *.web.app) keeps init.json's
// <project>.firebaseapp.com: preview hostnames can't be registered as OAuth
// redirect URIs in advance (custom-domains-and-hosting-promotion D2).
export const CUSTOM_DOMAINS: readonly string[] = [
  'civil-smart.com',
  'beta.civil-smart.com',
  'staging.civil-smart.com',
];

export function authDomainFor(hostname: string, configured: string): string {
  return CUSTOM_DOMAINS.includes(hostname) ? hostname : configured;
}

// Hosting can answer init.json with an error page while a freshly connected
// custom domain finishes provisioning. Retrying covers that blip; if it outlasts
// the retries, failing here beats booting on environment.prod.ts's empty config,
// which only surfaces later as a misleading `auth/invalid-api-key`.
export const INIT_JSON_ATTEMPTS = 3;
export const INIT_JSON_RETRY_DELAY_MS = 500;

async function fetchInitJson(): Promise<Partial<typeof environment.firebase>> {
  let lastError: unknown;
  for (let attempt = 1; attempt <= INIT_JSON_ATTEMPTS; attempt++) {
    try {
      const response = await fetch('/__/firebase/init.json');
      if (!response.ok) {
        throw new Error(`init.json responded ${response.status}`);
      }
      const config = await response.json();
      if (!config?.apiKey) {
        throw new Error('init.json has no apiKey');
      }
      return config;
    } catch (err) {
      lastError = err;
      if (attempt < INIT_JSON_ATTEMPTS) {
        await new Promise((resolve) =>
          setTimeout(resolve, INIT_JSON_RETRY_DELAY_MS * 2 ** (attempt - 1)),
        );
      }
    }
  }
  throw new Error(
    `Unable to load Firebase config after ${INIT_JSON_ATTEMPTS} attempts: ${
      lastError instanceof Error ? lastError.message : String(lastError)
    }`,
  );
}

@Injectable({
  providedIn: 'root',
})
export class InitializerService {
  private static env = environment;

  // constructor() {}
  static environment() {
    return InitializerService.env;
  }

  static async load(hostname = window.location.hostname): Promise<void> {
    const config = await fetchInitJson();
    console.log({ config });
    const firebase = { ...InitializerService.env.firebase, ...config };
    InitializerService.env.firebase = {
      ...firebase,
      authDomain: authDomainFor(hostname, firebase.authDomain),
    };
    environment.firebase = InitializerService.env.firebase;
    environment.mapId =
      environment.firebase.projectId === 'vms-web-app'
        ? '68e5e767b3c8d88f'
        : '65454638a9647646';
  }
}
